import { useState, useEffect } from "react";
import styled from "styled-components";

const Container = styled.div`
  display: flex;
  align-items: stretch;
  margin-bottom: 1rem;
  width: 100%;
  border: 1px solid
    ${({ theme, $error }) => ($error ? "red" : theme.colors.lineColor)};
  border-radius: 4px;
  background-color: ${({ theme }) => theme.colors.background};
  overflow: hidden;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus-within {
    border-color: ${({ $error, theme }) =>
      $error ? "red" : theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary}33;
  }
`;

const SelectWrapper = styled.div`
  position: relative;
  flex: ${({ $flex }) => $flex || 1};
  display: flex;
  align-items: center;

  &::after {
    content: "▾";
    position: absolute;
    right: 6px;
    font-size: 0.7rem;
    color: ${({ theme }) => theme.colors.lineColor};
    pointer-events: none;
  }
`;

const Divider = styled.div`
  width: 1px;
  background: ${({ theme }) => theme.colors.lineColor}55;
  align-self: stretch;
`;

const Select = styled.select`
  width: 100%;
  padding: 0.5rem 1.4rem 0.5rem 0.6rem;
  border: none;
  background: transparent;
  color: ${({ theme, $empty }) =>
    $empty ? theme.colors.lineColor : theme.colors.text};
  font-size: 0.9rem;
  font-family: ${({ theme }) => theme.typography.fontFamily};
  cursor: pointer;
  appearance: none;
  outline: none;

  option {
    background-color: ${({ theme }) => theme.colors.background};
    color: ${({ theme }) => theme.colors.text};
  }
`;

const MONTHS_HR = [
  "Siječanj", "Veljača", "Ožujak", "Travanj", "Svibanj", "Lipanj",
  "Srpanj", "Kolovoz", "Rujan", "Listopad", "Studeni", "Prosinac",
];

function daysInMonth(month, year) {
  if (!month || !year) return 31;
  return new Date(year, month, 0).getDate();
}

function parseValue(v) {
  if (!v) return { day: "", month: "", year: "" };
  const parts = v.split("-");
  return {
    year: parts[0] || "",
    month: parts[1] ? String(parseInt(parts[1], 10)) : "",
    day: parts[2] ? String(parseInt(parts[2], 10)) : "",
  };
}

function DatePicker({ name, value, onChange, $error, minYear, maxYear }) {
  const currentYear = new Date().getFullYear();
  const min = minYear ?? 1400;
  const max = maxYear ?? currentYear;

  const [internal, setInternal] = useState(() => parseValue(value));

  // Sync when external value is cleared/reset
  useEffect(() => {
    if (!value) setInternal({ day: "", month: "", year: "" });
  }, [value]);

  const { day, month, year } = internal;
  const maxDay = daysInMonth(month ? parseInt(month, 10) : 1, year ? parseInt(year, 10) : 2000);

  const years = [];
  for (let y = max; y >= min; y--) years.push(y);

  const handleSelect = (field, val) => {
    const next = { ...internal, [field]: val };
    setInternal(next);

    if (next.year && next.month && next.day) {
      const mm = String(next.month).padStart(2, "0");
      const dd = String(next.day).padStart(2, "0");
      onChange({ target: { name, value: `${next.year}-${mm}-${dd}` } });
    } else {
      onChange({ target: { name, value: "" } });
    }
  };

  return (
    <Container $error={$error}>
      <SelectWrapper $flex="1">
        <Select
          $empty={!day}
          value={day}
          onChange={(e) => handleSelect("day", e.target.value)}
        >
          <option value="">Dan</option>
          {Array.from({ length: maxDay }, (_, i) => i + 1).map((d) => (
            <option key={d} value={d}>{String(d).padStart(2, "0")}</option>
          ))}
        </Select>
      </SelectWrapper>

      <Divider />

      <SelectWrapper $flex="2">
        <Select
          $empty={!month}
          value={month}
          onChange={(e) => handleSelect("month", e.target.value)}
        >
          <option value="">Mjesec</option>
          {MONTHS_HR.map((m, i) => (
            <option key={i + 1} value={i + 1}>{m}</option>
          ))}
        </Select>
      </SelectWrapper>

      <Divider />

      <SelectWrapper $flex="2">
        <Select
          $empty={!year}
          value={year}
          onChange={(e) => handleSelect("year", e.target.value)}
        >
          <option value="">Godina</option>
          {years.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </Select>
      </SelectWrapper>
    </Container>
  );
}

export default DatePicker;
